// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState } from 'react';

import { type MappableWorkOrder, type AutodeskCloudModel, useEvolvePost } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import type { WithNullable } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { WorkOrderSearchableSelect } from 'modules/Shop/EvolveFabrication/WorkOrderSearchableSelect';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

type Props = {
  disabled?: boolean;
  spools: readonly RenderableSpool[];
  model: AutodeskCloudModel;
  refresh: () => void;
};

type ValidatedFormBody = {
  workOrder: MappableWorkOrder;
};

type FormBody = WithNullable<ValidatedFormBody>;

const AssignSpoolFields = ({ spools, model, refresh }: Pick<Props, 'spools' | 'model' | 'refresh'>) => {
  const workOrderFilters = useMemo(() => ({ projectIds: model.projectId }), [model.projectId]);
  const { apiCall: mapSpoolsApiCall } = useEvolvePost('shop/spool/map');
  const form = useForm<FormBody>({
    initialValues: {
      workOrder: null,
    },
    validate: {
      workOrder: (v) => isNil(v),
    },
  });

  const onSubmit = async (rawValues: typeof form.values) => {
    const { workOrder } = rawValues as ValidatedFormBody;
    await Promise.all(
      spools.map(async ({ name }) =>
        mapSpoolsApiCall({
          name,
          model,
          projectId: model.projectId,
          workOrderId: workOrder.workOrderId,
          // Only Revit can populate the BOM
          items: [],
        }),
      ),
    ).then(() => {
      refresh();
      notifications.show({
        title: 'Successfully assigned',
        message: `Assigned ${spools.length} spool${spools.length === 1 ? '' : 's'} to work order ${getDisplayNameWO(workOrder)}.`,
        color: 'green',
      });
    });
  };

  useFormPopover(form, onSubmit);

  return (
    <WorkOrderSearchableSelect
      label="Select Work Order"
      filters={workOrderFilters}
      required
      autoFocus
      data-autofocus
      {...form.getInputProps('workOrder')}
    />
  );
};

export const AssignSpoolToWorkOrder = ({ disabled, spools, model, refresh }: Props) => {
  const [opened, setOpened] = useState(false);

  return (
    <FormPopover
      opened={opened}
      onClose={() => setOpened(false)}
      confirmationText={`Assign ${spools.length} spool${spools.length === 1 ? '' : 's'}`}
      target={
        <Button
          size="xs"
          leftSection={<EvolveIcon icon="AttachToItem" size="sm" />}
          disabled={disabled || spools.length === 0}
          onClick={() => setOpened(true)}
        >
          Assign
        </Button>
      }
    >
      <AssignSpoolFields spools={spools} model={model} refresh={refresh} />
    </FormPopover>
  );
};
